import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Users, DollarSign, ShoppingCart, Activity, 
  Bell, Search, Settings, ChevronDown, ArrowUpRight, MoreHorizontal,
  Zap, Eye, Clock, Target, Sparkles, RefreshCw
} from 'lucide-react';

export default function DashboardPreview({ customization }) {
  const theme = customization?.theme || { primary: '#6366f1', secondary: '#8b5cf6', gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' };
  const companyName = customization?.companyName || 'Your Company';
  
  const [activeMetric, setActiveMetric] = useState(0);
  const [chartData, setChartData] = useState([45, 52, 68, 74, 62, 58, 71, 82, 88, 76, 84, 92]);
  const [liveUsers, setLiveUsers] = useState(847);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(2);
  const [hoveredBar, setHoveredBar] = useState(null);

  // Simulate live data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setChartData(prev => {
        const newData = [...prev.slice(1), Math.floor(Math.random() * 30) + 65];
        return newData;
      });
      setLiveUsers(prev => prev + Math.floor(Math.random() * 10) - 3);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const metrics = [
    { label: 'Revenue', value: '$127,432', change: '+14.2%', up: true, icon: DollarSign, sparkline: [40, 45, 42, 48, 52, 55, 58, 62] },
    { label: 'Users', value: '24,891', change: '+8.7%', up: true, icon: Users, sparkline: [30, 35, 38, 42, 40, 45, 48, 52] },
    { label: 'Orders', value: '3,842', change: '+22.4%', up: true, icon: ShoppingCart, sparkline: [20, 28, 32, 38, 42, 48, 55, 62] },
    { label: 'Conversion', value: '4.28%', change: '-0.3%', up: false, icon: Target, sparkline: [50, 48, 52, 46, 44, 42, 45, 43] },
  ];

  const recentActivity = [
    { user: 'Sarah K.', action: 'completed purchase', amount: '$249.00', time: '2m ago', avatar: '👩‍💼' },
    { user: 'Mike R.', action: 'signed up for Pro', amount: '$99/mo', time: '5m ago', avatar: '👨‍💻' },
    { user: 'Emma L.', action: 'upgraded plan', amount: '$199/mo', time: '8m ago', avatar: '👩‍🎨' },
  ];

  const maxValue = Math.max(...chartData);

  const handleRefresh = () => {
    setRefreshing(true);
    setChartData(prev => prev.map(() => Math.floor(Math.random() * 30) + 65));
    setTimeout(() => setRefreshing(false), 1000);
  };

  // Generate color variants from theme
  const getMetricColor = (index) => {
    const colors = [theme.primary, theme.secondary || theme.primary, '#f59e0b', '#ef4444'];
    return colors[index % colors.length];
  };

  return (
    <div style={{ 
      height: '100%', 
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      overflow: 'auto'
    }}>
      {/* Top Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        background: 'rgba(0,0,0,0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ 
            width: '32px', 
            height: '32px', 
            background: theme.gradient,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${theme.primary}40`
          }}>
            <Activity size={16} color="white" />
          </div>
          <div>
            <span style={{ color: 'white', fontWeight: '600', fontSize: '13px' }}>{companyName} Analytics</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ 
                width: '6px', height: '6px', borderRadius: '50%', 
                background: '#22c55e', 
                animation: 'pulse 2s infinite'
              }} />
              <span style={{ fontSize: '9px', color: '#94a3b8' }}>{liveUsers} online</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <motion.button 
            onClick={handleRefresh}
            whileTap={{ scale: 0.95 }}
            whileHover={{ background: 'rgba(255,255,255,0.1)' }}
            style={{ 
              background: 'rgba(255,255,255,0.05)', 
              border: 'none', 
              borderRadius: '6px', 
              padding: '6px',
              cursor: 'pointer',
              display: 'flex'
            }}
          >
            <motion.div animate={refreshing ? { rotate: 360 } : {}} transition={{ duration: 1 }}>
              <RefreshCw size={14} color="#64748b" />
            </motion.div>
          </motion.button>
          <div style={{ position: 'relative' }}>
            <Bell size={16} color="#64748b" />
            <span style={{
              position: 'absolute', top: '-4px', right: '-4px',
              width: '14px', height: '14px', borderRadius: '50%',
              background: '#ef4444', fontSize: '8px', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: '600'
            }}>3</span>
          </div>
          <div style={{
            width: '28px', height: '28px', borderRadius: '50%',
            background: theme.gradient,
            border: '2px solid rgba(255,255,255,0.2)'
          }} />
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '12px 16px', overflowY: 'auto', height: 'calc(100% - 52px)' }}>
        {/* Header with AI Insight */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 style={{ color: 'white', fontSize: '16px', fontWeight: '600', margin: '0 0 2px 0' }}>
                Dashboard
              </h1>
              <p style={{ color: '#64748b', fontSize: '10px', margin: 0 }}>
                Real-time business intelligence
              </p>
            </div>
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              style={{
                background: `linear-gradient(135deg, ${theme.primary}20, ${theme.primary}10)`,
                border: `1px solid ${theme.primary}40`,
                borderRadius: '8px',
                padding: '8px 10px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Sparkles size={12} color={theme.primary} />
              <span style={{ fontSize: '9px', color: theme.primary, fontWeight: '500' }}>
                AI: Revenue up 23% vs prediction
              </span>
            </motion.div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '10px',
          marginBottom: '14px'
        }}>
          {metrics.map((metric, i) => {
            const Icon = metric.icon;
            const color = getMetricColor(i);
            const isActive = activeMetric === i;
            return (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ scale: 1.02, y: -2 }}
                onClick={() => setActiveMetric(i)}
                style={{
                  background: isActive 
                    ? `linear-gradient(135deg, ${color}20, ${color}10)`
                    : 'rgba(255,255,255,0.03)',
                  borderRadius: '10px',
                  padding: '12px 10px',
                  cursor: 'pointer',
                  border: isActive 
                    ? `1px solid ${color}40` 
                    : '1px solid rgba(255,255,255,0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '6px',
                    background: `${color}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Icon size={14} color={color} />
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '2px',
                    color: metric.up ? '#10b981' : '#ef4444',
                    fontSize: '9px', fontWeight: '600'
                  }}>
                    {metric.up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                    {metric.change}
                  </div>
                </div>
                <div style={{ color: 'white', fontSize: '16px', fontWeight: '700', marginBottom: '2px' }}>
                  {metric.value}
                </div>
                <div style={{ color: '#64748b', fontSize: '9px' }}>{metric.label}</div>
                
                {/* Mini sparkline */}
                <div style={{ 
                  display: 'flex', alignItems: 'flex-end', gap: '2px', 
                  height: '20px', marginTop: '6px', opacity: 0.6 
                }}>
                  {metric.sparkline.map((val, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ height: 0 }}
                      animate={{ height: `${(val / 70) * 100}%` }}
                      transition={{ delay: 0.3 + idx * 0.05 }}
                      style={{
                        flex: 1,
                        background: color,
                        borderRadius: '1px',
                        minHeight: '2px'
                      }}
                    />
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Main Chart */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '12px',
          padding: '14px',
          border: '1px solid rgba(255,255,255,0.05)',
          marginBottom: '14px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <h3 style={{ color: 'white', fontSize: '12px', fontWeight: '600', margin: 0 }}>Revenue Overview</h3>
              <p style={{ color: '#64748b', fontSize: '9px', margin: '2px 0 0 0' }}>Click on bars for details</p>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {['1D', '1W', '1M', '1Y'].map((period, idx) => (
                <motion.button 
                  key={period} 
                  onClick={() => setSelectedPeriod(idx)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '4px 8px',
                    background: idx === selectedPeriod ? `${theme.primary}30` : 'transparent',
                    border: idx === selectedPeriod ? `1px solid ${theme.primary}40` : '1px solid transparent',
                    borderRadius: '4px',
                    color: idx === selectedPeriod ? theme.primary : '#64748b',
                    fontSize: '9px',
                    cursor: 'pointer',
                    fontWeight: idx === selectedPeriod ? '600' : '400'
                  }}
                >{period}</motion.button>
              ))}
            </div>
          </div>
          
          {/* Interactive Chart */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '100px', position: 'relative' }}>
            {chartData.map((value, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${(value / maxValue) * 100}%` }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                onMouseEnter={() => setHoveredBar(i)}
                onMouseLeave={() => setHoveredBar(null)}
                onClick={() => setActiveMetric(i % 4)}
                style={{
                  flex: 1,
                  background: hoveredBar === i || i === chartData.length - 1
                    ? theme.gradient
                    : `linear-gradient(180deg, ${theme.primary}80 0%, ${theme.primary}30 100%)`,
                  borderRadius: '3px 3px 0 0',
                  minHeight: '4px',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
              >
                {/* Tooltip on hover */}
                <AnimatePresence>
                  {hoveredBar === i && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 5, scale: 0.9 }}
                      style={{
                        position: 'absolute',
                        top: '-32px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: theme.primary,
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '9px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        zIndex: 10,
                        boxShadow: `0 4px 12px ${theme.primary}60`
                      }}
                    >
                      ${(value * 1.2).toFixed(0)}k
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', color: '#475569', fontSize: '8px' }}>
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(m => (
              <span key={m}>{m}</span>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '12px',
          padding: '12px',
          border: '1px solid rgba(255,255,255,0.05)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 style={{ margin: 0, fontSize: '11px', fontWeight: '600', color: 'white' }}>Recent Activity</h3>
            <button style={{ background: 'none', border: 'none', color: theme.primary, fontSize: '9px', cursor: 'pointer' }}>
              View all →
            </button>
          </div>
          {recentActivity.map((activity, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + i * 0.1 }}
              whileHover={{ background: 'rgba(255,255,255,0.03)', x: 4 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 4px',
                borderRadius: '8px',
                borderBottom: i < recentActivity.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <span style={{ fontSize: '18px' }}>{activity.avatar}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', color: 'white' }}>
                  <strong>{activity.user}</strong> {activity.action}
                </div>
                <div style={{ fontSize: '9px', color: '#64748b' }}>{activity.time}</div>
              </div>
              <span style={{ fontSize: '10px', color: theme.primary, fontWeight: '600' }}>{activity.amount}</span>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
